import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from '@mui/material';
import { ConfirmPhraseText, DELETE_FOREVER_PHRASE } from 'components/deletion/DeleteForeverDialog';

export type PortalDeletionPreviewData = {
  portalID?: string;
  portalName?: string;
  stackName?: string;
  dynamoCounts?: Record<string, string> | string;
  pgvectorRowCount?: string;
};

const parseDynamoCounts = (
  dynamoCounts: PortalDeletionPreviewData['dynamoCounts'],
): Record<string, string> => {
  if (!dynamoCounts) return {};
  if (typeof dynamoCounts === 'string') {
    try {
      return JSON.parse(dynamoCounts) as Record<string, string>;
    } catch {
      return {};
    }
  }
  return dynamoCounts;
};

const PreviewBody = ({ data }: { data: PortalDeletionPreviewData }) => {
  const dynamoCounts = parseDynamoCounts(data.dynamoCounts);
  const dynamoEntries = Object.entries(dynamoCounts)
    .sort(([a], [b]) => a.localeCompare(b));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Typography variant="body2">
        Portal
        {' '}
        <strong>{data.portalName || data.portalID}</strong>
        {' '}
        and all portal-scoped data will be permanently deleted. This cannot be undone.
      </Typography>

      {data.stackName && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>CloudFormation stack</Typography>
          <Typography variant="body2">{data.stackName}</Typography>
        </Box>
      )}

      {dynamoEntries.length > 0 && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>DynamoDB records (approx.)</Typography>
          <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
            {dynamoEntries.map(([table, count]) => (
              <Typography component="li" variant="body2" key={table}>
                {table}
                :
                {' '}
                {count ?? '0'}
              </Typography>
            ))}
          </Box>
        </Box>
      )}

      {data.pgvectorRowCount != null && data.pgvectorRowCount !== '' && (
        <Typography variant="body2">
          pgvector:
          {' '}
          {data.pgvectorRowCount}
        </Typography>
      )}
    </Box>
  );
};

type PortalDeleteDialogProps = {
  open: boolean;
  portalName?: string;
  loading: boolean;
  loadError?: string;
  previewData: PortalDeletionPreviewData | null;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
};

const PortalDeleteDialog = ({
  open,
  portalName,
  loading,
  loadError,
  previewData,
  onClose,
  onConfirm,
}: PortalDeleteDialogProps) => {
  const [phrase, setPhrase] = useState('');
  const [busy, setBusy] = useState(false);
  const matched = phrase === DELETE_FOREVER_PHRASE;

  useEffect(() => {
    if (!open) setPhrase('');
  }, [open]);

  const handleClose = () => {
    if (busy) return;
    setPhrase('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!matched || busy || loading || !previewData) return;
    setBusy(true);
    try {
      await onConfirm();
      setPhrase('');
      onClose();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Delete portal
        {portalName ? `: ${portalName}` : ''}
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 3 }}>
            <CircularProgress size={24} />
            <Typography variant="body2">Loading deletion preview…</Typography>
          </Box>
        )}
        {!loading && loadError && (
          <Typography color="error" variant="body2">{loadError}</Typography>
        )}
        {!loading && !loadError && previewData && (
          <>
            <PreviewBody data={previewData} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 3, mb: 1 }}>
              Type
              {' '}
              <ConfirmPhraseText />
              {' '}
              to confirm:
            </Typography>
            <TextField
              fullWidth
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
              disabled={busy}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>Cancel</Button>
        <Button
          color="error"
          variant="contained"
          disabled={loading || !!loadError || !previewData || !matched || busy}
          onClick={() => { handleConfirm().catch(() => {}); }}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PortalDeleteDialog;
